import { useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import {
  formatBoolean,
  formatCurrency,
  formatIban,
  formatTransactionAmount,
  normalizeIban,
} from 'common/utils/formatUtils';
import { format } from 'date-fns';
import {
  FinancialProductType,
  RetirementPensionSavingsProductFragment,
  useRetirementPensionSavingsProductQuery,
  useUpdateRetirementPensionSavingsProductMutation,
} from 'generated/graphql';
import { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductDetail } from '../ProductDetail/ProductDetail';
import { QRCodeDialog, QRCodeDialogProps } from '../QRCodeDialog/QRCodeDialog';
import { QRCodeMissingDataDialog, QRCodeMissingDataDialogProps } from '../QRCodeDialog/QRCodeMissingDataDialog';
import { ButtonRowType, DetailRowType, Section } from '../types';
import { ProductDetailDialogProps } from '../useProductDetail';
import { mapRetirementPensionSavingsProductToInput } from './utils';

export const RetirementPensionSavingsProductDetail: React.FC<ProductDetailDialogProps> = ({ id, ...props }) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();

  const { data } = useRetirementPensionSavingsProductQuery({
    variables: {
      id,
    },
  });

  const qrCodeDialog = useDialog<QRCodeDialogProps, void>(QRCodeDialog, {
    instanceId: 'qr-code-dialog',
    tmpNavigable: true,
  });

  const qrCodeMissingDataDialog = useDialog<QRCodeMissingDataDialogProps, void>(QRCodeMissingDataDialog, {
    instanceId: 'qr-code-missing-data-dialog',
  });

  const handleQRPayment = () => {
    const iban = data?.detail.paymentInfo?.ibanForPayment;
    const amount = data?.detail.paymentPeriod?.expectedAmount?.amount;
    if (iban && amount) {
      qrCodeDialog.create({
        amount,
        constantSymbol: data?.detail.paymentInfo?.constantSymbol ?? undefined,
        currency: data?.detail.paymentPeriod?.expectedAmount?.currency ?? 'EUR',
        iban,
        specificSymbol: data?.detail.paymentInfo?.specificSymbol ?? undefined,
        variableSymbol: data?.detail.paymentInfo?.variableSymbol ?? undefined,
      });
    } else {
      qrCodeMissingDataDialog.create({});
    }
  };

  const periodicityOptions = usePeriodicityOptions();

  const content: Section<RetirementPensionSavingsProductFragment, Path<RetirementPensionSavingsProductFragment>>[] = [
    {
      rows: [
        {
          inputType: 'text',
          label: t('products:retirementPensionSavings.detail.institutionName'),
          path: 'institutionName',
          skip: !!data?.detail.institution?.id,
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'institutionName'>,
        {
          inputType: 'text',
          label: t('products:retirementPensionSavings.detail.name'),
          path: 'name',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'name'>,
        {
          inputType: 'text',
          label: t('products:retirementPensionSavings.detail.contractNumber'),
          path: 'contractNumber',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'contractNumber'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:retirementPensionSavings.detail.contractSignDate'),
          path: 'contractSignDate',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'contractSignDate'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:retirementPensionSavings.detail.contractEndDate'),
          path: 'contractEndDate',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'contractEndDate'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          formatter: (value: number) =>
            formatTransactionAmount(value, i18n.language, 2, data?.detail.paymentPeriod?.expectedAmount?.currency),
          inputEndAdornment: data?.detail.paymentPeriod?.expectedAmount?.currency,
          inputType: 'amount',
          label: t('products:retirementPensionSavings.detail.instalment'),
          // we are storing paymentPeriod.expectedAmount.amount as negative number
          // but we want user to type a positive equivalent
          parse: (value) => -value,
          path: 'paymentPeriod.expectedAmount.amount',
          transform: (value) => -value,
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'paymentPeriod.expectedAmount.amount'>,
        {
          formatter: (value: Date) => format(value, 'd. M. yyyy'),
          inputType: 'date',
          label: t('products:retirementPensionSavings.detail.nextPayment'),
          path: 'paymentPeriod.expectedDate',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'paymentPeriod.expectedDate'>,
        {
          formatter: (value) =>
            periodicityOptions.find(
              (option) =>
                option.value.periodCount === value.periodCount && option.value.periodTypeCode === value.periodTypeCode,
            )?.label ?? '',
          inputType: 'radioSelect',
          label: t('products:retirementPensionSavings.detail.periodType'),
          options: periodicityOptions,
          parse: (value) => ({
            expectedAmount: data?.detail.paymentPeriod?.expectedAmount,
            expectedDate: data?.detail.paymentPeriod?.expectedDate,
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
          path: 'paymentPeriod',
          transform: (value) => ({
            periodCount: value.periodCount,
            periodTypeCode: value.periodTypeCode,
          }),
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'paymentPeriod'>,
      ],
      title: '',
    },
    {
      rows: [
        {
          inputType: 'variableSymbol',
          label: t('products:retirementPensionSavings.detail.variableSymbol'),
          path: 'paymentInfo.variableSymbol',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'paymentInfo.variableSymbol'>,
        {
          inputType: 'specificSymbol',
          label: t('products:retirementPensionSavings.detail.specificSymbol'),
          path: 'paymentInfo.specificSymbol',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'paymentInfo.specificSymbol'>,
        {
          inputType: 'constantSymbol',
          label: t('products:retirementPensionSavings.detail.constantSymbol'),
          path: 'paymentInfo.constantSymbol',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'paymentInfo.constantSymbol'>,
        {
          formatter: formatIban,
          inputType: 'iban',
          label: t('products:retirementPensionSavings.detail.ibanForPayment'),
          parse: normalizeIban,
          path: 'paymentInfo.ibanForPayment',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'paymentInfo.ibanForPayment'>,
        {
          color: 'blue.dark',
          label: t('products:retirementPensionSavings.detail.qrCode'),
          onClick: handleQRPayment,
        } as ButtonRowType,
      ],
      title: t('products:retirementPensionSavings.detail.paymentInfo.title'),
    },
    {
      rows: [
        {
          formatter: (value: number) => formatCurrency(value ?? 0, i18n.language),
          inputEndAdornment: t('common:inputEndAdornment.eur'),
          inputType: 'amount',
          label: t('products:retirementPensionSavings.detail.currentMarketValue'),
          path: 'currentMarketValue.amount',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'currentMarketValue.amount'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:retirementPensionSavings.detail.guaranteedFund'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'guaranteedFund',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'guaranteedFund'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:retirementPensionSavings.detail.nonGuaranteedFund'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'nonGuaranteedFund',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'nonGuaranteedFund'>,
        {
          formatter: (value: boolean) => formatBoolean(value, t),
          inputType: 'radioSelect',
          label: t('products:retirementPensionSavings.detail.voluntaryContribution'),
          options: [
            {
              label: t('common:boolean.true'),
              value: true,
            },
            {
              label: t('common:boolean.false'),
              value: false,
            },
          ],
          path: 'voluntaryContribution',
        } as DetailRowType<RetirementPensionSavingsProductFragment, 'voluntaryContribution'>,
      ],
      title: t('products:retirementPensionSavings.detail.financialDetails.title', {
        date: format(data?.detail.financialDetailsUpdatedAt ?? new Date(), 'd. M. yyyy'),
      }),
    },
  ];

  const [updateProduct] = useUpdateRetirementPensionSavingsProductMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['CalendarEventInstances', 'Notifications', 'NotificationsCount'],
  });

  const handleUpdate = async (value: RetirementPensionSavingsProductFragment) => {
    await updateProduct({ variables: { id, updatedProduct: mapRetirementPensionSavingsProductToInput(value) } });
  };

  return (
    <ProductDetail<RetirementPensionSavingsProductFragment>
      {...props}
      content={content}
      data={data?.detail}
      institution={data?.detail.institution ?? undefined}
      onUpdate={handleUpdate}
      title={data?.detail.name ?? t('products:retirementPensionSavings.detail.title')}
      type={FinancialProductType.RetirementPensionSavings}
    />
  );
};
